import React, { useState, useEffect, memo } from 'react';
import { FormControl, FormLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';

/**
 * Represents a single selectable option in the toggle button group.
 */
interface Choice {
    /** Value submitted when this option is selected. */
    id: string | number;
    /** Display label rendered on the button. */
    name: string;
}

/**
 * Props for {@link MToggleButtonGroup}.
 */
interface MToggleButtonGroupProps {
    /** Optional field label rendered above the button group. */
    label?: string;
    /** Array of selectable options. */
    choices: Choice[];
    /**
     * Pre-selected value applied on first render.
     * Changing this prop after mount re-applies the default.
     */
    defaultValue?: string | number;
    /** Controlled value.  When provided, the component becomes controlled. */
    value?: string | number;
    /**
     * Called with the new value whenever the selection changes.
     * Receives an empty string `''` when the active button is deselected.
     *
     * @param value - The newly selected option id, or `''` when deselected.
     */
    onChange?: (value: string | number) => void;
    /** Button size passed to MUI ToggleButtonGroup. Defaults to `'small'`. */
    size?: 'small' | 'medium' | 'large';
}

/**
 * A stateful, exclusive MUI {@link ToggleButtonGroup} control that mirrors the
 * `value` / `defaultValue` / `onChange` contract used by every other control in
 * the `components/controls` family (MSelect, MRadio, etc.).
 *
 * @example
 * ```tsx
 * <MToggleButtonGroup
 *     label="Type"
 *     choices={[{ id: '', name: 'All' }, { id: 'GA', name: 'GA' }, { id: 'Reserved', name: 'Reserved' }]}
 *     defaultValue=""
 *     onChange={(val) => setEventType(val as string)}
 * />
 * ```
 */
const MToggleButtonGroup: React.FC<MToggleButtonGroupProps> = ({
    label,
    choices,
    defaultValue,
    value,
    onChange,
    size = 'small',
    ...props
}) => {
    const [mValue, setMValue] = useState<string | number>('');

    useEffect(() => {
        if (defaultValue !== undefined) {
            setMValue(defaultValue);
        }
    }, [defaultValue]);

    useEffect(() => {
        if (value !== undefined) setMValue(value);
    }, [value]);

    const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string | number | null) => {
        // When clicking the already-selected button MUI passes null — treat as deselect (empty string)
        const next: string | number = newValue ?? '';
        setMValue(next);
        if (onChange) onChange(next);
    };

    return (
        <FormControl size={size as any}>
            {label && (
                <FormLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>{label}</FormLabel>
            )}
            <ToggleButtonGroup
                size={size}
                value={mValue}
                exclusive
                onChange={handleChange}
                {...props}
            >
                {choices.map((choice) => (
                    <ToggleButton key={choice.id} value={choice.id}>
                        {choice.name}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </FormControl>
    );
};

export default memo(MToggleButtonGroup);
